import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

const emptySets = () =>
  Array.from({ length: 5 }, () => ({ home: "", guest: "" }));

const emptySubmatch = (seq = 1) => ({
  id: null,
  seq,
  math_type: 1,
  home_player_1: "",
  home_player_2: "",
  guest_player_1: "",
  guest_player_2: "",
  sets: emptySets(),
  home_result: null,
  guest_result: null,
});

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const TeamTournamentDetail = () => {
  const { uuid } = useParams();
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [schedule, setSchedule] = useState({ groups: [], knockout: [] });
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ type: "", msg: "" });
  const [openTieId, setOpenTieId] = useState(null);
  const [tieDetail, setTieDetail] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamSearch, setTeamSearch] = useState("");
  const [knockoutForm, setKnockoutForm] = useState({
    round_name: "",
    home_team_id: "",
    guest_team_id: "",
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [detailRes, scheduleRes] = await Promise.all([
        axios.get(`${API_URL}/team-tournaments/${uuid}`),
        axios.get(`${API_URL}/team-tournaments/${uuid}/schedule`),
      ]);
      setTournament(detailRes.data.tournament);
      setTeams(detailRes.data.teams);
      setSchedule({
        groups: scheduleRes.data.groups,
        knockout: scheduleRes.data.knockout,
      });
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Không tải được giải đồng đội.",
      });
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    axios
      .get(`${API_URL}/playerspublic?limit=all`)
      .then((res) => setPlayers(res.data))
      .catch(() => setPlayers([]));
  }, []);

  const loadTie = useCallback(async (tieId) => {
    const res = await axios.get(`${API_URL}/team-tournaments/tie/${tieId}`);
    setTieDetail(res.data);
    setDrafts(res.data.submatches.map((item) => ({ ...item, sets: item.sets.map((set) => ({ ...set })) })));
  }, []);

  const toggleTie = async (tieId) => {
    if (openTieId === tieId) {
      setOpenTieId(null);
      setTieDetail(null);
      setDrafts([]);
      return;
    }
    try {
      setTieDetail(null);
      setDrafts([]);
      setOpenTieId(tieId);
      await loadTie(tieId);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Không tải được trận đội.",
      });
    }
  };

  const handleApprove = async (teamId) => {
    try {
      await axios.put(
        `${API_URL}/team-tournaments/${uuid}/team/${teamId}/approve`,
        {},
        authHeader()
      );
      await loadAll();
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Không duyệt được đội.",
      });
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm("Xoá đội này? Các trận của đội cũng bị xoá.")) return;
    try {
      await axios.delete(
        `${API_URL}/team-tournaments/${uuid}/team/${teamId}`,
        authHeader()
      );
      await loadAll();
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Không xoá được đội.",
      });
    }
  };

  const handleSaveTeam = async () => {
    try {
      await axios.put(
        `${API_URL}/team-tournaments/${uuid}/team/${editingTeam.id}`,
        { name: editingTeam.name, member_ids: editingTeam.member_ids },
        authHeader()
      );
      setEditingTeam(null);
      await loadAll();
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Không cập nhật được đội.",
      });
    }
  };

  const updateDraft = (index, patch) => {
    setDrafts((list) =>
      list.map((item, i) => (i === index ? { ...item, ...patch } : item))
    );
  };

  const updateDraftSet = (index, setIndex, side, value) => {
    setDrafts((list) =>
      list.map((item, i) => {
        if (i !== index) return item;
        const sets = item.sets.map((set, s) =>
          s === setIndex ? { ...set, [side]: value } : set
        );
        return { ...item, sets };
      })
    );
  };

  const addDraft = () => {
    if (!tieDetail) return;
    if (drafts.length >= tieDetail.max_submatches) return;
    const nextSeq = drafts.reduce((max, item) => Math.max(max, Number(item.seq)), 0) + 1;
    setDrafts([...drafts, emptySubmatch(nextSeq)]);
  };

  const handleSaveDraft = async (index) => {
    const draft = drafts[index];
    if (!tieDetail) return;
    const body = {
      seq: Number(draft.seq),
      math_type: Number(draft.math_type),
      home_player_1: draft.home_player_1 || null,
      home_player_2: draft.math_type === 2 ? draft.home_player_2 || null : null,
      guest_player_1: draft.guest_player_1 || null,
      guest_player_2: draft.math_type === 2 ? draft.guest_player_2 || null : null,
      sets: draft.sets,
    };
    try {
      let saved;
      if (draft.id) {
        const res = await axios.put(
          `${API_URL}/team-tournaments/tie/${tieDetail.id}/submatch/${draft.id}`,
          body,
          authHeader()
        );
        saved = res.data;
      } else {
        const res = await axios.post(
          `${API_URL}/team-tournaments/tie/${tieDetail.id}/submatch`,
          body,
          authHeader()
        );
        saved = res.data;
      }
      setDrafts((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, ...saved, sets: saved.sets.map((set) => ({ ...set })) }
            : item
        )
      );
      setNotification({ type: "success", msg: "Đã lưu trận con." });
      await loadAll();
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Không lưu được trận con.",
      });
    }
  };

  const handleDeleteDraft = async (index) => {
    const draft = drafts[index];
    if (!draft.id) {
      setDrafts(drafts.filter((_, i) => i !== index));
      return;
    }
    if (!window.confirm("Xoá trận con này?")) return;
    try {
      await axios.delete(
        `${API_URL}/team-tournaments/tie/${tieDetail.id}/submatch/${draft.id}`,
        authHeader()
      );
      setDrafts((prev) => prev.filter((_, i) => i !== index));
      await loadAll();
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Không xoá được trận con.",
      });
    }
  };

  const handleResetGroups = async () => {
    if (
      !window.confirm(
        "Xoá toàn bộ trận vòng bảng (kèm trận con) và mở lại đăng ký? Dùng khi cần đổi số đội hoặc số đội mỗi bảng rồi chia lại."
      )
    ) {
      return;
    }
    try {
      const res = await axios.delete(
        `${API_URL}/team-tournaments/${uuid}/group-ties`,
        authHeader()
      );
      setNotification({
        type: "success",
        msg: `Đã xoá ${res.data.deleted} trận vòng bảng, giải trở về trạng thái mở đăng ký.`,
      });
      setOpenTieId(null);
      setTieDetail(null);
      setDrafts([]);
      await loadAll();
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Không xoá được vòng bảng.",
      });
    }
  };

  const handleCreateKnockout = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/team-tournaments/${uuid}/tie`,
        { ...knockoutForm, stage: "knockout" },
        authHeader()
      );
      setKnockoutForm({ round_name: "", home_team_id: "", guest_team_id: "" });
      await loadAll();
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Không tạo được trận loại trực tiếp.",
      });
    }
  };

  const handleDeleteTie = async (tieId) => {
    if (!window.confirm("Xoá trận đội này?")) return;
    try {
      await axios.delete(`${API_URL}/team-tournaments/tie/${tieId}`, authHeader());
      await loadAll();
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Không xoá được trận đội.",
      });
    }
  };

  const playerOptions = (team) =>
    (team?.members || []).map((member) => (
      <option key={member.id} value={member.id}>
        {member.name}
      </option>
    ));

  const filteredPlayers = useMemo(() => {
    const term = teamSearch.trim().toLowerCase();
    if (!term) return players.slice(0, 60);
    return players
      .filter(
        (player) =>
          String(player.id).includes(term) ||
          String(player.name || "").toLowerCase().includes(term)
      )
      .slice(0, 60);
  }, [players, teamSearch]);

  const renderTie = (tie) => (
    <Card key={tie.id} className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong>{tie.home.name}</strong> vs <strong>{tie.guest.name}</strong>
            <div className="text-muted small">
              {tie.round_name}
              {tie.play_date ? ` · ${tie.play_date}` : ""}
            </div>
          </div>
          <div className="text-end">
            <Badge bg={tie.entered_count ? "success" : "secondary"} className="me-2">
              {tie.entered_count ? `${tie.home_wins}–${tie.guest_wins}` : "Chưa nhập"}
            </Badge>
            <Button size="sm" variant="outline-primary" onClick={() => toggleTie(tie.id)}>
              {openTieId === tie.id ? "Đóng" : "Nhập kết quả"}
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              className="ms-2"
              onClick={() => handleDeleteTie(tie.id)}
            >
              Xoá trận
            </Button>
          </div>
        </div>

        {openTieId === tie.id && tieDetail && tieDetail.id === tie.id && (
          <div className="mt-3">
            <div className="mb-2 text-muted small">
              Đội nhà: {tieDetail.home.name} · Đội khách: {tieDetail.guest.name} ·
              Tối đa {tieDetail.max_submatches} trận con
            </div>
            {drafts.map((draft, index) => (
              <Row className="g-2 align-items-end mb-2" key={draft.id || `new-${index}`}>
                <Col xs={6} md={1}>
                  <Form.Label className="small mb-0">Trận</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min={1}
                    value={draft.seq}
                    onChange={(e) => updateDraft(index, { seq: e.target.value })}
                  />
                </Col>
                <Col xs={6} md={1}>
                  <Form.Label className="small mb-0">Loại</Form.Label>
                  <Form.Select
                    size="sm"
                    value={draft.math_type}
                    onChange={(e) =>
                      updateDraft(index, { math_type: Number(e.target.value) })
                    }
                  >
                    <option value={1}>Đơn</option>
                    <option value={2}>Đôi</option>
                  </Form.Select>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label className="small mb-0">VĐV nhà</Form.Label>
                  <Form.Select
                    size="sm"
                    value={draft.home_player_1 || ""}
                    onChange={(e) =>
                      updateDraft(index, { home_player_1: e.target.value })
                    }
                  >
                    <option value="">--</option>
                    {playerOptions(tieDetail.home)}
                  </Form.Select>
                  {Number(draft.math_type) === 2 && (
                    <Form.Select
                      size="sm"
                      className="mt-1"
                      value={draft.home_player_2 || ""}
                      onChange={(e) =>
                        updateDraft(index, { home_player_2: e.target.value })
                      }
                    >
                      <option value="">--</option>
                      {playerOptions(tieDetail.home)}
                    </Form.Select>
                  )}
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label className="small mb-0">VĐV khách</Form.Label>
                  <Form.Select
                    size="sm"
                    value={draft.guest_player_1 || ""}
                    onChange={(e) =>
                      updateDraft(index, { guest_player_1: e.target.value })
                    }
                  >
                    <option value="">--</option>
                    {playerOptions(tieDetail.guest)}
                  </Form.Select>
                  {Number(draft.math_type) === 2 && (
                    <Form.Select
                      size="sm"
                      className="mt-1"
                      value={draft.guest_player_2 || ""}
                      onChange={(e) =>
                        updateDraft(index, { guest_player_2: e.target.value })
                      }
                    >
                      <option value="">--</option>
                      {playerOptions(tieDetail.guest)}
                    </Form.Select>
                  )}
                </Col>
                <Col xs={12} md={4}>
                  <Form.Label className="small mb-0">Sec</Form.Label>
                  <div className="d-flex gap-1">
                    {draft.sets.map((set, setIndex) => (
                      <div key={setIndex} className="d-flex flex-column" style={{ width: 46 }}>
                        <Form.Control
                          size="sm"
                          value={set.home ?? ""}
                          onChange={(e) =>
                            updateDraftSet(index, setIndex, "home", e.target.value)
                          }
                        />
                        <Form.Control
                          size="sm"
                          className="mt-1"
                          value={set.guest ?? ""}
                          onChange={(e) =>
                            updateDraftSet(index, setIndex, "guest", e.target.value)
                          }
                        />
                      </div>
                    ))}
                  </div>
                </Col>
                <Col xs={12} md={2}>
                  <Button size="sm" variant="success" onClick={() => handleSaveDraft(index)}>
                    Lưu
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="ms-2"
                    onClick={() => handleDeleteDraft(index)}
                  >
                    Xoá
                  </Button>
                  {draft.home_result !== null && draft.home_result !== undefined && (
                    <div className="small text-muted mt-1">
                      {draft.home_result}–{draft.guest_result}
                    </div>
                  )}
                </Col>
              </Row>
            ))}
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={addDraft}
              disabled={drafts.length >= tieDetail.max_submatches}
            >
              Thêm trận con
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  if (loading && !tournament) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!tournament) {
    return <Alert variant="danger">Không tìm thấy giải đồng đội.</Alert>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h2 className="fw-bold mb-1">{tournament.name}</h2>
          <p className="text-muted mb-0">
            {tournament.start_date || "Chưa có ngày"} ·{" "}
            {tournament.location || "Chưa có địa điểm"} ·{" "}
            {tournament.status === "registration_open"
              ? "Đang mở đăng ký"
              : "Đã chia bảng"}
          </p>
        </div>
        {tournament.status !== "registration_open" && (
          <Button variant="outline-danger" size="sm" onClick={handleResetGroups}>
            Xoá vòng bảng & mở lại đăng ký
          </Button>
        )}
      </div>

      {notification.msg && (
        <Alert
          variant={notification.type}
          dismissible
          onClose={() => setNotification({ type: "", msg: "" })}
        >
          {notification.msg}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header className="fw-bold">
          Đội ({teams.length}/{tournament.max_teams})
        </Card.Header>
        <ListGroup variant="flush">
          {teams.length === 0 && (
            <ListGroup.Item className="text-muted">Chưa có đội nào.</ListGroup.Item>
          )}
          {teams.map((team) => (
            <ListGroup.Item key={team.id}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{team.name}</strong>{" "}
                  <Badge bg={team.approved ? "success" : "secondary"}>
                    {team.approved ? "Đã duyệt" : "Chờ duyệt"}
                  </Badge>
                  <div className="text-muted small">
                    {team.members.map((member) => member.name).join(", ")}
                  </div>
                </div>
                <div>
                  <Button size="sm" variant="info" onClick={() => handleApprove(team.id)}>
                    {team.approved ? "Hủy duyệt" : "Duyệt"}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="ms-2"
                    onClick={() => {
                      setEditingTeam({
                        id: team.id,
                        name: team.name,
                        member_ids: [...team.member_ids],
                      });
                      setTeamSearch("");
                    }}
                  >
                    Sửa đội
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    className="ms-2"
                    onClick={() => handleDeleteTeam(team.id)}
                  >
                    Xoá
                  </Button>
                </div>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Card>

      {schedule.groups.map((group) => (
        <div key={group.name} className="mb-4">
          <h4 className="fw-bold">{group.name}</h4>
          <div className="text-muted small mb-2">
            {group.teams.map((team) => team.name).join(" · ")}
          </div>
          {group.ties.map(renderTie)}
        </div>
      ))}

      <Card className="mb-4">
        <Card.Header className="fw-bold">Loại trực tiếp</Card.Header>
        <Card.Body>
          {schedule.knockout.length === 0 && (
            <p className="text-muted">Chưa có trận loại trực tiếp.</p>
          )}
          {schedule.knockout.map(renderTie)}

          <Form className="mt-3" onSubmit={handleCreateKnockout}>
            <Row className="g-2 align-items-end">
              <Col md={3}>
                <Form.Label className="small mb-0">Tên vòng</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="Tứ kết / Bán kết / Chung kết"
                  value={knockoutForm.round_name}
                  onChange={(e) =>
                    setKnockoutForm({ ...knockoutForm, round_name: e.target.value })
                  }
                  required
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small mb-0">Đội 1</Form.Label>
                <Form.Select
                  size="sm"
                  value={knockoutForm.home_team_id}
                  onChange={(e) =>
                    setKnockoutForm({ ...knockoutForm, home_team_id: e.target.value })
                  }
                  required
                >
                  <option value="">--</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small mb-0">Đội 2</Form.Label>
                <Form.Select
                  size="sm"
                  value={knockoutForm.guest_team_id}
                  onChange={(e) =>
                    setKnockoutForm({ ...knockoutForm, guest_team_id: e.target.value })
                  }
                  required
                >
                  <option value="">--</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Button size="sm" type="submit">
                  Tạo trận loại trực tiếp
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {editingTeam && (
        <Modal show onHide={() => setEditingTeam(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Sửa đội</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Tên đội</Form.Label>
              <Form.Control
                value={editingTeam.name}
                onChange={(e) =>
                  setEditingTeam({ ...editingTeam, name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>
                Thành viên ({editingTeam.member_ids.length}/{tournament.max_team_members})
              </Form.Label>
              <Form.Control
                size="sm"
                placeholder="Tìm theo tên hoặc ID"
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
              />
            </Form.Group>
            <div style={{ maxHeight: 260, overflowY: "auto" }}>
              {filteredPlayers.map((player) => (
                <Form.Check
                  key={player.id}
                  type="checkbox"
                  id={`edit-team-player-${player.id}`}
                  label={`${player.name} (ID: ${player.id})`}
                  checked={editingTeam.member_ids.includes(player.id)}
                  onChange={(e) => {
                    const ids = e.target.checked
                      ? [...editingTeam.member_ids, player.id]
                      : editingTeam.member_ids.filter((id) => id !== player.id);
                    setEditingTeam({ ...editingTeam, member_ids: ids });
                  }}
                />
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditingTeam(null)}>
              Đóng
            </Button>
            <Button variant="primary" onClick={handleSaveTeam}>
              Lưu
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default TeamTournamentDetail;
